import * as utils from '@core_n/common/utils';
import * as units from '@core_n/common/units';
import { SensorDisplayCoreObject } from '@core_n/asp/SensorDisplayCoreObject';
import { AspObject } from '@core_n/asp/AspObject';
import { NAT_NAI_COUNT, SensorObject } from './SensorObject';
import { IObjectsDrawer } from '@core_n/asp/interfaces';
import { userSettings } from '@core_n/common/settings';
import { AppConfig } from '@core_n/app.config';
import { SensorCoreObject } from '@core_n/asp/SensorCoreObject';

export enum SensorDataType {
  Coverage = 'coverage',
  NatNaiArea = 'natNai',
  Azimuth = 'azimuth',
  AzimuthB = 'azimuthB',
  AzimuthC = 'azimuthC',
  }

export class SensorDisplayObject extends SensorDisplayCoreObject {
  naiActive: boolean;
  natActive: boolean;
  naiColor = [1, 1, 1, 1];
  natColor = [1, 1, 1, 1];
  natFillColor: any;
  naiFillColor: any;
  natFillOpacity: number;
  naiFillOpacity: number;
  globalOpacity: any;
  snsOpacity: any;
  symSize: number;

  nainatCount: number = undefined;

  constructor(aspObject: AspObject, drawer: IObjectsDrawer) {
    super(aspObject, drawer);
    this.naiActive = userSettings.naiActive;
    this.natActive = userSettings.natActive;
    this.naiColor = utils.cssColorToNewmi(userSettings.naiColor);
    this.natColor = utils.cssColorToNewmi(userSettings.natColor);
    this.globalOpacity = userSettings.nainatOpacity;
    this.snsOpacity = userSettings.pointOpacity;
    this.natFillOpacity = userSettings.natFillOpacity;
    this.naiFillOpacity = userSettings.naiFillOpacity;
    this.natFillColor = [this.natColor[0], this.natColor[1], this.natColor[2], this.natFillOpacity];
    this.naiFillColor = [this.naiColor[0], this.naiColor[1], this.naiColor[2], this.naiFillOpacity];
    this.symSize = userSettings.symbolsSize;
    this.nainatCount = (aspObject as SensorCoreObject).naiNatArray.length;
  }

  // Override SensorDisplayCoreObject
  onDataChange(): void {
    let sensorObject = this.aspObject as SensorObject;

    if (sensorObject) {
      if (!sensorObject.active())
        return;
    }
    else
      return;

    // this bit needs to be updated to not redraw all the time...
    // update drawing only when angle values change... or aro is open/close...
    if (sensorObject.coverage) {

      const geometry = new NeWMI.Geometry.Polyline();
      geometry.points.push(new NeWMI.Geometry.Point({ x: 34.784, y: 32.092 }));
      geometry.points.push(new NeWMI.Geometry.Point({ x: 34.855, y: 32.077 }));


      if (sensorObject.coverage.shape && sensorObject.showCoverage) {
        this.drawData[SensorDataType.Coverage] =
          this.createGeometryDrawData(sensorObject.coverage.shape, 2, utils.newmiDarkerColor(sensorObject.getColor(), 0.65));
        if (this.isVisible()) {
          this.drawer.addObjectToDm(this, [SensorDataType.Coverage], [SensorDataType.Coverage]);
        }
      }
      else {
        if (this.drawData[SensorDataType.Coverage] != null)
          this.drawer.removeObjectFromDm(this, [SensorDataType.Coverage], [SensorDataType.Coverage]);
        this.drawData[SensorDataType.Coverage] = null;
      }
    }

    if (sensorObject.azimuth != null && sensorObject.geometry) {
      const distMeters = AppConfig.settings.params.azimuth?.video?.length ?? 1000; // meters
      const openDegree = AppConfig.settings.params.azimuth?.video?.openDegree ?? 30; // meters
      var azimuthDeg = sensorObject.azimuth;
      if (sensorObject.isAcoustic) {
        azimuthDeg = sensorObject.azimuth * units.RAD_TO_DEG;
      }
      azimuthDeg = azimuthDeg % 360;
      
      const bisectorDegree = openDegree / 2;
      const secondPoint = utils.newmiPointByRangeAzimuth(sensorObject.geometry, distMeters, azimuthDeg - bisectorDegree, 0);
      const secondPointB = utils.newmiPointByRangeAzimuth(sensorObject.geometry, distMeters, azimuthDeg + bisectorDegree, 0);
    
      const centerPoint: NeWMI.Geometry.Point = new NeWMI.Geometry.Point(sensorObject.geometry);
       
       const NinetyDeg = Math.PI / 2;

       const fromAngle = -Math.atan2(secondPoint.y-centerPoint.y,secondPoint.x-centerPoint.x) + NinetyDeg;
       const toAngle = -Math.atan2(secondPointB.y-centerPoint.y,secondPointB.x-centerPoint.x) + NinetyDeg;
       const radiusRad = utils.distanceMeterToDeg(centerPoint, distMeters);


       const geometryCloseLine = new NeWMI.Geometry.Sector({
                    x: centerPoint.x,
                    y: centerPoint.y,
                    outerRadius: radiusRad,
                    innerRadius: 0,
                    fromAngle,
                    toAngle
                });

     
      const color = sensorObject.getColor();
      const colorWithAlpha = [color[0],color[1],color[2],0.1];

       this.drawData[SensorDataType.AzimuthC] =
         this.createGeometryDrawData(geometryCloseLine, 2, utils.newmiDarkerColor(color, 0.65),
                utils.newmiDarkerColor(colorWithAlpha, 0.65));

      if (this.isVisible()) {
         this.drawer.addObjectToDm(this, [SensorDataType.AzimuthC], [SensorDataType.AzimuthC]);
        
      }
    }
    else {
      if (this.drawData[SensorDataType.AzimuthC] != null) 
               this.drawer.removeObjectFromDm(this, [SensorDataType.AzimuthC], [SensorDataType.AzimuthC]);
         this.drawData[SensorDataType.AzimuthC] = null;
    }

    // remove nainat trash
    if (sensorObject.naiNatDeleteList.length > 0) {
      for (let i = 0; i < sensorObject.naiNatDeleteList.length; i++) {
        let id = sensorObject.naiNatDeleteList[i];
        this.drawer.removeObjectFromDm(this, [SensorDataType.NatNaiArea + id], [SensorDataType.NatNaiArea]);
        this.drawData[SensorDataType.NatNaiArea + id] = null;
      }
      sensorObject.naiNatDeleteList = [];
      this.drawer.getLayer().refresh();
    }

    if (sensorObject.naiNatArray.length > 0) {
      let colorNai = [this.naiColor[0], this.naiColor[1], this.naiColor[2], this.globalOpacity];
      let colorNat = [this.natColor[0], this.natColor[1], this.natColor[2], this.globalOpacity];
      for (let i = 0; i < sensorObject.naiNatArray.length; i++) {
        let id = sensorObject.naiNatArray[i].naiNatId;
        if (sensorObject.naiNatArray[i].naiNatShape != null) {
          if (!sensorObject.isNatNaiVisible || !sensorObject.naiNatArray[i].isVisible)
            this.drawData[SensorDataType.NatNaiArea + id] = this.createGeometryDrawData(sensorObject.naiNatArray[i].naiNatShape, 0);
          else if (sensorObject.naiNatArray[i].naiOrNat == 1) // nai
            this.drawData[SensorDataType.NatNaiArea + id] = this.naiActive ? this.createGeometryDrawData(sensorObject.naiNatArray[i].naiNatShape, 3, colorNai, this.naiFillColor) : null;
          else if (sensorObject.naiNatArray[i].naiOrNat == 2)  // nat
            this.drawData[SensorDataType.NatNaiArea + id] = this.natActive ? this.createGeometryDrawData(sensorObject.naiNatArray[i].naiNatShape, 3, colorNat, this.natFillColor) : null;
          else
            this.drawData[SensorDataType.NatNaiArea + id] = null;
        }
        else
          this.drawData[SensorDataType.NatNaiArea + id] = null;

        if (this.isVisible() || sensorObject.naiNatArray[i].isNew) {
          sensorObject.naiNatArray[i].isNew = false;
          this.drawer.addObjectToDm(this, [SensorDataType.NatNaiArea + id], [SensorDataType.NatNaiArea]);
        }

        if (sensorObject.naiNatArray[i].needsUpdate) {
          sensorObject.naiNatArray[i].needsUpdate = false;
          this.drawer.updateObjectInDm(this, [SensorDataType.NatNaiArea + id], [SensorDataType.NatNaiArea]);
        }
      }
    }
    super.onDataChange();
  }

  prepareToDraw(): void {
    super.prepareToDraw();
    let colorNai = utils.cssColorToNewmi(userSettings.naiColor);
    let colorNat = utils.cssColorToNewmi(userSettings.natColor);
    let snsopacity = userSettings.pointOpacity;
    if (this.globalOpacity != userSettings.nainatOpacity ||
      this.naiColor.toString() != colorNai.toString() ||
      this.natColor.toString() != colorNat.toString() ||
      this.naiActive != userSettings.naiActive ||
      this.natActive != userSettings.natActive ||
      this.naiFillOpacity != userSettings.naiFillOpacity ||
      this.natFillOpacity != userSettings.natFillOpacity ||
      this.snsOpacity != snsopacity ||
      this.symSize != userSettings.symbolsSize) {
      this.symSize = userSettings.symbolsSize;
      this.naiActive = userSettings.naiActive;
      this.natActive = userSettings.natActive;
      this.globalOpacity = userSettings.nainatOpacity;
      this.naiColor = utils.cssColorToNewmi(userSettings.naiColor);
      this.natColor = utils.cssColorToNewmi(userSettings.natColor);
      this.natFillOpacity = this.globalOpacity * userSettings.natFillOpacity;
      this.naiFillOpacity = this.globalOpacity * userSettings.naiFillOpacity;
      this.natFillColor = [this.natColor[0], this.natColor[1], this.natColor[2], this.natFillOpacity];
      this.naiFillColor = [this.naiColor[0], this.naiColor[1], this.naiColor[2], this.naiFillOpacity];
      this.onDataChange();
    }
    return;
  }

}
